import React, { useEffect, useState } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";
import FieldNoteCard from "../packageSidebar/FieldNoteCard";
import { cleanRichHtml } from "../packageDetails/FieldNoteRichBody";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

function FieldNoteAuthor() {
  const { authorSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${process.env.REACT_APP_API_FOLDER}get_fieldnote_author/?slug=${encodeURIComponent(
          authorSlug
        )}`;
        const response = await axios.get(url, {
          headers: { "X-API-TOKEN": API_TOKEN },
        });

        if (cancelled) return;
        if (response.data && response.data.error) {
          setError(response.data.error);
          setData(null);
        } else {
          setData(response.data);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Failed to load this author.");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authorSlug]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (loading) {
    return (
      <div className="package-sidebar-wrapper pt-110 fieldnote-author-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <Skeleton height={54} width="45%" />
              <div className="mt-3"><Skeleton count={4} /></div>
              <div className="row g-4 mt-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div className="col-lg-4 col-md-6" key={index}><Skeleton height={350} /></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data || !data.author) {
    return (
      <div className="package-sidebar-wrapper pt-110 fieldnote-author-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <Link className="fieldnote-back-link" to="/fieldnotes" onClick={scrollTop}>
                <i className="bi bi-arrow-left" aria-hidden="true" /> Field Notes
              </Link>
              <h2 className="mt-4">Author unavailable</h2>
              <p>{error || "This Field Notes author could not be found."}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { author } = data;
  const notes = Array.isArray(data.notes) ? data.notes : [];

  return (
    <div className="package-sidebar-wrapper pt-110 fieldnote-author-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <Link className="fieldnote-back-link" to="/fieldnotes" onClick={scrollTop}>
              <i className="bi bi-arrow-left" aria-hidden="true" /> Field Notes
            </Link>

            <header className="fieldnote-author-profile">
              <h1>{author.name}</h1>
              {author.biog && (
                <div
                  className="workshop_bodytext fieldnote-author-profile-biog"
                  dangerouslySetInnerHTML={{ __html: cleanRichHtml(author.biog) }}
                />
              )}
              {author.link && (
                <a
                  className="fieldnote-author-link"
                  href={author.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                >
                  {author.link}
                </a>
              )}
            </header>

            <section className="fieldnote-author-notes" aria-labelledby="fieldnote-author-notes-title">
              <h2 id="fieldnote-author-notes-title">Field Notes by {author.name}</h2>
              <div className="row g-4">
                {notes.map((fieldNote) => (
                  <div className="col-lg-4 col-md-6 fadeffect" key={fieldNote.id}>
                    <FieldNoteCard fieldNote={fieldNote} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FieldNoteAuthor;
